// AddIocModal.jsx 🧠➕ Clean + Fully Styled
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import TagInput from "./TagInput";
import "./styles/AddIocModal.css";

export default function AddIocModal({ show, onHide, onSuccess }) {
  const [iocType, setIocType] = useState("IP");
  const [iocValue, setIocValue] = useState("");
  const [description, setDescription] = useState("");
  const [criticality, setCriticality] = useState("1");
  const [status, setStatus] = useState("pending");
  const [affectedCountry, setAffectedCountry] = useState("");
  const [relatedToIncident, setRelatedToIncident] = useState(false);
  const [tags, setTags] = useState([]);
  const [file, setFile] = useState(null);
  const [duplicateFound, setDuplicateFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const detectIoCType = (val) => {
    val = val.trim();
    if (/^(https?|ftp):\/\/[^\s]+$/i.test(val)) return "URL";
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Email";
    if (/^(\d{1,3}\.){3}\d{1,3}$/.test(val)) return "IP";
    if (/^([a-f0-9]{1,4}:){7}[a-f0-9]{1,4}$/i.test(val)) return "IP";
    if (/^[a-f0-9]{32}$|^[a-f0-9]{40}$|^[a-f0-9]{64}$/i.test(val)) return "Hash";
    if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)) return "Domain";
    if (/^\+?[1-9]\d{0,2}[\s-]?(\(?\d{1,4}\)?[\s-]?)?[\d\s-]{5,15}$/.test(val)) return "Phone";
    return "Unknown";
  };

  const resetForm = () => {
    setIocType("IP");
    setIocValue("");
    setDescription("");
    setCriticality("1");
    setStatus("pending");
    setAffectedCountry("");
    setRelatedToIncident(false);
    setTags([]);
    setFile(null);
    setMessage(null);
    setDuplicateFound(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);

    if (duplicateFound) {
      setMessage({ type: "error", text: "This IoC already exists." });
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("ioc_type", iocType);
      formData.append("ioc_value", iocValue);
      formData.append("description", description);
      formData.append("criticality", criticality);
      formData.append("status", status);
      formData.append("affected_country", affectedCountry);
      formData.append("related_to_incident", relatedToIncident ? 1 : 0);
      formData.append("tags", JSON.stringify(tags));
      if (file) formData.append("file_attachment", file);

      const res = await axios.post("/api/add_ioc.php", formData);
      if (res.data.success) {
        setMessage({ type: "success", text: res.data.message });
        resetForm();
        onSuccess?.();
        onHide();
      } else {
        throw new Error(res.data.error || "Unknown error");
      }
    } catch (err) {
      setMessage({ type: "error", text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      dialogClassName="add-ioc-modal"
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-shield-lock-fill me-2 text-warning" /> Add an IoC
            </h5>
            <button className="btn-close btn-close-white" onClick={onHide}></button>
          </div>

          <div className="modal-body">
            {/* IoC Type */}
            <div className="mb-3">
              <label className="form-label">IoC Type</label>
              <select className="form-select" value={iocType} onChange={(e) => setIocType(e.target.value)} required>
                <option value="IP">IP</option>
                <option value="Domain">Domain</option>
                <option value="URL">URL</option>
                <option value="Hash">Hash</option>
                <option value="Email">Email</option>
                <option value="Phone">Phone</option>
              </select>
            </div>

            {/* IoC Value */}
            <div className="mb-3">
              <label className="form-label">IoC Value</label>
              <input
                className="form-control"
                value={iocValue}
                autoComplete="off"
                onChange={async (e) => {
                  const val = e.target.value.trim();
                  setIocValue(val);
                  const type = detectIoCType(val);
                  if (type !== "Unknown") setIocType(type);
                  if (val.length > 3) {
                    try {
                      const res = await axios.get(`/api/check_ioc_exists.php?value=${encodeURIComponent(val)}`);
                      setDuplicateFound(res.data.exists);
                    } catch {
                      setDuplicateFound(false);
                    }
                  } else {
                    setDuplicateFound(false);
                  }
                }}
                required
              />
              {duplicateFound && <small className="text-danger">This IoC already exists.</small>}
            </div>

            {/* Description */}
            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
              />
              <small className="text-secondary">Maximum 1000 characters</small>
            </div>

            {/* Criticality / Status */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Criticality</label>
                <select className="form-select" value={criticality} onChange={(e) => setCriticality(e.target.value)}>
                  <option value="1">Low</option>
                  <option value="2">Medium</option>
                  <option value="3">High</option>
                  <option value="4">Critical</option>
                  <option value="5">Severe</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="pending">Pending</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="closed">Closed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* Country */}
            <div className="mb-3">
              <label className="form-label">Affected Country</label>
              <select className="form-select" value={affectedCountry} onChange={(e) => setAffectedCountry(e.target.value)}>
                <option value="">Select a country</option>
                <option value="FR">France</option>
                <option value="DE">Germany</option>
                <option value="US">United States</option>
                <option value="BE">Belgium</option>
                <option value="ES">Spain</option>
                <option value="IT">Italy</option>
                <option value="CH">Switzerland</option>
                <option value="IE">Ireland</option>
                <option value="GR">Greece</option>
              </select>
            </div>

            {/* Related checkbox */}
            <div className="form-check mb-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="relatedCheck"
                checked={relatedToIncident}
                onChange={() => setRelatedToIncident(!relatedToIncident)}
              />
              <label className="form-check-label" htmlFor="relatedCheck">
                Related to incident?
              </label>
            </div>

            {/* File upload */}
            <div className="mb-3">
              <label className="form-label">File Attachment (optional)</label>
              <input
                type="file"
                className="form-control"
                accept=".csv,.xls,.pdf"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </div>

            {/* Tags */}
            <div className="mb-3">
              <label className="form-label">Tags</label>
              <TagInput tags={tags} setTags={setTags} />
            </div>

            {/* Message */}
            {message && (
              <div className={`alert alert-${message.type === "success" ? "success" : "danger"} mt-3`}>
                {message.text}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-profile" onClick={onHide} disabled={submitting}>
              Close
            </button>
            <button type="submit" className="btn-profile btn-success" disabled={submitting}>
              {submitting ? "Adding..." : "Add"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
